import React, { useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Rating,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import LinkIcon from '@mui/icons-material/Link'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import BlockIcon from '@mui/icons-material/Block'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import API from '../../../../services/API'
import { GlobalContext } from '../../../../App'
import { EventMetaContext } from '../SingleEvent'

// Client feedback without an account. Planners mint a private, single-use
// link for someone at the client and either email it from AmbuNet or copy it
// into their own email / WhatsApp. Responses land here and are appended to
// the Post-event tab's Client Feedback. Server:
// routers/eventPlanning/eventFeedbackRequests.js

const EXPIRY_OPTIONS = [
  { days: 14, label: '14 days' },
  { days: 30, label: '30 days (recommended)' },
  { days: 60, label: '60 days' },
  { days: 90, label: '90 days' },
]

const STATE_CHIP = {
  open: { label: 'Awaiting feedback', color: 'warning' },
  used: { label: 'Received', color: 'success' },
  expired: { label: 'Expired', color: 'default' },
  revoked: { label: 'Cancelled', color: 'default' },
}

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch (e) {
    return false
  }
}

const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value ?? '').trim())

const emailToast = (email, fallback) =>
  email?.success
    ? email.muzzled
      ? 'Emails are switched off on this demo organisation — copy the link instead.'
      : fallback
    : 'The email could not be sent — copy the link and send it yourself.'

const LinkField = ({ link, onCopied }) => (
  <TextField
    value={link}
    fullWidth
    size="small"
    label="Feedback link"
    InputProps={{
      readOnly: true,
      startAdornment: (
        <InputAdornment position="start">
          <LinkIcon fontSize="small" />
        </InputAdornment>
      ),
      endAdornment: (
        <InputAdornment position="end">
          <Tooltip title="Copy link">
            <IconButton size="small" onClick={async () => onCopied?.(await copyToClipboard(link))}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </InputAdornment>
      ),
    }}
    onFocus={(e) => e.target.select()}
  />
)

const RequestDialog = ({ open, onClose, eventMeta, config, onCreated }) => {
  const { setToast } = useContext(GlobalContext)
  const [contact, setContact] = useState(null)
  const [recipientName, setRecipientName] = useState('')
  const [recipientEmail, setRecipientEmail] = useState('')
  const [message, setMessage] = useState('')
  const [expiresInDays, setExpiresInDays] = useState(config?.defaultDays ?? 30)
  const [sendEmail, setSendEmail] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [created, setCreated] = useState(null)

  const suggestions = config?.suggestions ?? []

  const pick = (suggestion) => {
    setContact(suggestion?.contact ?? null)
    setRecipientName(suggestion?.name ?? '')
    setRecipientEmail(suggestion?.email ?? '')
  }

  useEffect(() => {
    if (!open) return
    pick(suggestions[0])
    setMessage('')
    setExpiresInDays(config?.defaultDays ?? 30)
    setSendEmail(true)
    setSaving(false)
    setError(null)
    setCreated(null)
  }, [open])

  const emailOk = !recipientEmail.trim() || isEmail(recipientEmail)
  const willEmail = sendEmail && recipientEmail.trim().length > 0
  const canSubmit = recipientName.trim().length > 0 && emailOk && !saving

  const submit = () => {
    setSaving(true)
    setError(null)
    API.post(`/event/${eventMeta._id}/feedback-requests`, {
      contact,
      recipientName: recipientName.trim(),
      recipientEmail: recipientEmail.trim(),
      message: message.trim(),
      expiresInDays,
      sendEmail: willEmail,
    })
      .then((res) => {
        if (res.status === 200) {
          setCreated(res.data)
          onCreated?.(res.data.request)
          if (res.data?.email && !res.data.email.success) {
            setToast({
              message: 'Link created, but the email could not be sent — copy the link and send it yourself.',
            })
          }
        } else {
          setError(res.data?.message || res.data?.error || 'Unable to create the link. Please try again.')
        }
      })
      .catch(() => setError('Unable to create the link. Please try again.'))
      .finally(() => setSaving(false))
  }

  const selectedSuggestion = suggestions.findIndex(
    (s) => s.email.toLowerCase() === recipientEmail.trim().toLowerCase(),
  )

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>
        {created ? 'Feedback link ready' : 'Request client feedback'}
      </DialogTitle>
      <DialogContent dividers>
        {created ? (
          <Stack spacing={2}>
            <Alert severity="success">
              <AlertTitle>Link ready for {created.request?.recipientName}</AlertTitle>
              {created.email?.success
                ? created.email.muzzled
                  ? 'Emails are switched off on this demo organisation — copy the link below to share it.'
                  : `We've emailed it to ${created.request?.recipientEmail}. You can also copy it below and send it by text or WhatsApp.`
                : 'Copy the link below and send it however suits the client: email, text or WhatsApp.'}{' '}
              It expires on <strong>{moment(created.request?.expires).format('Do MMM YYYY')}</strong>{' '}
              and can be used once.
            </Alert>
            <LinkField
              link={created.request?.link ?? ''}
              onCopied={(ok) =>
                setToast({
                  message: ok ? 'Link copied to clipboard.' : 'Unable to copy — select the link and copy it manually.',
                })
              }
            />
          </Stack>
        ) : (
          <Stack spacing={2.5}>
            <Typography variant="body2" color="text.secondary">
              The client gets a private link to a short feedback form: star ratings and a couple
              of comment boxes. They don't need an AmbuNet account. They'll only see this
              event's name and dates.
            </Typography>
            {eventMeta?.endDate && moment().isBefore(eventMeta.endDate) && (
              <Alert severity="info" variant="outlined">
                This event hasn't finished yet. Feedback requests work best sent soon after it ends.
              </Alert>
            )}
            {error && <Alert severity="error">{error}</Alert>}

            {suggestions.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  CLIENT CONTACTS
                </Typography>
                <Stack direction="row" gap={1} flexWrap="wrap" mt={0.75}>
                  {suggestions.map((suggestion, index) => (
                    <Tooltip key={suggestion.email} title={suggestion.email}>
                      <Chip
                        label={[suggestion.name || suggestion.email, suggestion.detail]
                          .filter(Boolean)
                          .join(' · ')}
                        onClick={() => pick(suggestion)}
                        color={index === selectedSuggestion ? 'primary' : 'default'}
                        variant={index === selectedSuggestion ? 'filled' : 'outlined'}
                        sx={{ borderRadius: 1.5, fontWeight: 600 }}
                      />
                    </Tooltip>
                  ))}
                </Stack>
              </Box>
            )}

            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Name"
                    value={recipientName}
                    onChange={(e) => {
                      setRecipientName(e.target.value)
                      setContact(null)
                    }}
                    required
                    fullWidth
                    size="small"
                    autoComplete="off"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Email"
                    value={recipientEmail}
                    onChange={(e) => {
                      setRecipientEmail(e.target.value)
                      setContact(null)
                    }}
                    fullWidth
                    size="small"
                    autoComplete="off"
                    error={!emailOk}
                    helperText={!emailOk ? 'Enter a valid email address' : 'Optional if you will share the link yourself'}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Personal message (optional)"
                    placeholder="e.g. Thanks again for having us at the festival. We'd love to hear how it went from your side."
                    value={message}
                    onChange={(e) => setMessage(e.target.value.slice(0, 1000))}
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    helperText="Shown in the email and at the top of the form"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label="Link expires after"
                    value={expiresInDays}
                    onChange={(e) => setExpiresInDays(Number(e.target.value))}
                    fullWidth
                    size="small"
                    helperText={`Expires ${moment().add(expiresInDays, 'days').format('Do MMM YYYY')}`}
                  >
                    {EXPIRY_OPTIONS.filter((option) => option.days <= (config?.maxDays ?? 90)).map((option) => (
                      <MenuItem key={option.days} value={option.days}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={willEmail}
                        onChange={(e) => setSendEmail(e.target.checked)}
                        disabled={recipientEmail.trim().length === 0}
                        color="success"
                      />
                    }
                    label="Email the link now"
                  />
                </Grid>
              </Grid>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {created ? (
          <Button variant="contained" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={onClose} color="inherit" disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={submit}
              disabled={!canSubmit}
              startIcon={willEmail ? <MailOutlineIcon /> : <LinkIcon />}
            >
              {saving ? 'Creating…' : willEmail ? 'Create & email link' : 'Create link'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

const ResponseDetail = ({ response, categories }) => (
  <Stack gap={1} sx={{ pt: 1 }}>
    {categories
      .filter(({ key }) => response.ratings?.[key])
      .map(({ key, label }) => (
        <Stack key={key} direction="row" justifyContent="space-between" alignItems="center" gap={1}>
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
          <Rating value={response.ratings[key]} readOnly size="small" />
        </Stack>
      ))}
    {response.bookAgain && (
      <Typography variant="caption" color="text.secondary">
        Would book again: <strong>{response.bookAgain}</strong>
      </Typography>
    )}
    {response.wentWell && (
      <Box>
        <Typography variant="caption" fontWeight={700}>
          What went well
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
          {response.wentWell}
        </Typography>
      </Box>
    )}
    {response.couldImprove && (
      <Box>
        <Typography variant="caption" fontWeight={700}>
          What could improve
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
          {response.couldImprove}
        </Typography>
      </Box>
    )}
    {response.testimonialConsent && (
      <Chip
        size="small"
        color="success"
        variant="outlined"
        label="Happy to be quoted"
        sx={{ alignSelf: 'flex-start', borderRadius: 1, fontWeight: 700 }}
      />
    )}
  </Stack>
)

const RequestRow = ({ request, categories, onCopy, onResend, onRevoke }) => {
  const [expanded, setExpanded] = useState(false)
  const chip = STATE_CHIP[request.state] ?? STATE_CHIP.expired
  const response = request.response
  // A lapsed link nobody cancelled can be reopened by sending a reminder.
  const canRemind = request.state === 'open' || (request.state === 'expired' && request.status === 'Pending')

  const meta =
    request.state === 'used'
      ? `Received ${moment(request.completedAt).format('Do MMM YYYY')}`
      : request.state === 'revoked'
        ? `Cancelled ${moment(request.revokedAt).format('Do MMM YYYY')}`
        : request.state === 'expired'
          ? `Expired ${moment(request.expires).format('Do MMM YYYY')}`
          : `Expires ${moment(request.expires).fromNow()}`

  return (
    <Box
      sx={{
        px: 1,
        py: 1,
        borderRadius: 2,
        '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.03)' },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        onClick={response ? () => setExpanded((prev) => !prev) : undefined}
        sx={{ cursor: response ? 'pointer' : 'default' }}
      >
        <Box minWidth={0} flex={1}>
          <Typography variant="body2" fontWeight={700} noWrap>
            {response?.respondentName || request.recipientName}
            {response?.respondentRole && (
              <Typography component="span" variant="caption" color="text.secondary">
                {' '}
                · {response.respondentRole}
              </Typography>
            )}
          </Typography>
          <Typography component="div" variant="caption" color="text.secondary" noWrap>
            {[
              meta,
              request.state === 'open' && request.emailSentAt && 'emailed',
              request.state === 'open' && request.openedCount > 0 && 'opened',
            ]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
        </Box>
        {response ? (
          <>
            <Rating value={response.overallRating} readOnly size="small" />
            <ExpandMoreIcon
              fontSize="small"
              sx={{
                color: 'text.secondary',
                transition: 'transform 0.2s',
                transform: expanded ? 'rotate(180deg)' : 'none',
              }}
            />
          </>
        ) : (
          <Chip
            size="small"
            label={chip.label}
            color={chip.color}
            variant="outlined"
            sx={{ borderRadius: 1, height: 22, fontSize: '0.68rem', fontWeight: 700, flexShrink: 0 }}
          />
        )}
      </Stack>

      {response && (
        <Collapse in={expanded} unmountOnExit>
          <ResponseDetail response={response} categories={categories} />
        </Collapse>
      )}

      {canRemind && (
        <Stack direction="row" gap={0.5} mt={0.5} flexWrap="wrap">
          {request.state === 'open' && (
            <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => onCopy(request)}>
              Copy link
            </Button>
          )}
          <Button size="small" startIcon={<MailOutlineIcon />} onClick={() => onResend(request)}>
            {request.state === 'expired' ? 'Reopen & email' : request.emailSentAt ? 'Remind' : 'Email'}
          </Button>
          <Button size="small" color="inherit" startIcon={<BlockIcon />} onClick={() => onRevoke(request)}>
            Cancel
          </Button>
        </Stack>
      )}
    </Box>
  )
}

const ResendDialog = ({ request, onClose, onSend }) => {
  const [email, setEmail] = useState('')
  const [extend, setExtend] = useState(false)
  const [busy, setBusy] = useState(false)
  const lapsed = request?.state === 'expired'
  useEffect(() => {
    setEmail(request?.recipientEmail ?? '')
    setExtend(false)
    setBusy(false)
  }, [request])
  return (
    <Dialog open={!!request} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>
        {lapsed ? 'Reopen and email the link' : 'Email the feedback link'}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            size="small"
            error={email.length > 0 && !isEmail(email)}
          />
          {lapsed ? (
            <Typography variant="body2" color="text.secondary">
              The link has expired. Sending it will reopen it for another 30 days.
            </Typography>
          ) : (
            <FormControlLabel
              control={<Switch checked={extend} onChange={(e) => setExtend(e.target.checked)} />}
              label="Extend expiry to 30 days from now"
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!isEmail(email) || busy}
          onClick={async () => {
            setBusy(true)
            await onSend({ recipientEmail: email.trim(), extendDays: extend || lapsed ? 30 : undefined })
            setBusy(false)
          }}
        >
          Send
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const ClientFeedbackPanel = () => {
  const { eventMeta } = useContext(EventMetaContext)
  const { setToast } = useContext(GlobalContext)
  const [config, setConfig] = useState(null)
  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [resending, setResending] = useState(null)

  useEffect(() => {
    if (!eventMeta?._id) return
    setLoading(true)
    API.get(`/event/${eventMeta._id}/feedback-requests`)
      .then((res) => {
        if (res.status === 200) {
          setConfig({
            defaultDays: res.data.defaultDays,
            maxDays: res.data.maxDays,
            categories: res.data.categories ?? [],
            suggestions: res.data.suggestions ?? [],
          })
          setRequests(res.data.requests ?? [])
        }
      })
      .finally(() => setLoading(false))
  }, [eventMeta?._id])

  const replace = (updated) => setRequests((prev) => prev.map((r) => (r._id === updated._id ? updated : r)))

  const responses = useMemo(() => requests.filter((r) => r.response), [requests])
  const average = responses.length
    ? responses.reduce((sum, r) => sum + r.response.overallRating, 0) / responses.length
    : null

  const handleCopy = async (request) => {
    const ok = await copyToClipboard(request.link)
    setToast({ message: ok ? 'Link copied to clipboard.' : 'Unable to copy automatically.' })
  }

  const handleResend = async (payload) => {
    const res = await API.post(`/event/${eventMeta._id}/feedback-requests/${resending._id}/resend`, payload)
    if (res.status === 200) {
      replace(res.data.request)
      setToast({ message: emailToast(res.data.email, 'Feedback link emailed.') })
      setResending(null)
    } else {
      setToast({ message: res.data?.message || 'Unable to send the email.' })
    }
  }

  const handleRevoke = async (request) => {
    const res = await API.post(`/event/${eventMeta._id}/feedback-requests/${request._id}/revoke`)
    if (res.status === 200) {
      replace(res.data.request)
      setToast({ message: 'Feedback link cancelled.' })
    } else {
      setToast({ message: res.data?.message || 'Unable to cancel the link.' })
    }
  }

  return (
    <Box
      component={Paper}
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: '1px solid rgba(15, 23, 42, 0.08)',
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.05)',
        bgcolor: 'background.paper',
      }}
    >
      <Stack gap={1.5}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
          <Stack gap={0.25}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Client feedback
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {average != null
                ? `${average.toFixed(1)} / 5 from ${responses.length} response${responses.length === 1 ? '' : 's'}`
                : 'Ask the organiser how it went, no account needed'}
            </Typography>
          </Stack>
          <Tooltip title="Request feedback">
            <span>
              <IconButton
                size="small"
                color="primary"
                aria-label="Request feedback"
                onClick={() => setDialogOpen(true)}
                disabled={!config}
              >
                <RateReviewOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

        {!loading && requests.length === 0 ? (
          <Stack
            alignItems="center"
            gap={1}
            sx={{
              py: 2.5,
              px: 2,
              borderRadius: 2,
              border: '1px dashed rgba(15, 23, 42, 0.18)',
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" fontWeight={700}>
              No feedback requested yet
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Send the client a private link to a two-minute feedback form. Email it from here or
              copy it into a text or WhatsApp.
            </Typography>
            <Button
              size="small"
              variant="contained"
              startIcon={<RateReviewOutlinedIcon />}
              onClick={() => setDialogOpen(true)}
              disabled={!config}
              sx={{ mt: 0.5 }}
            >
              Request feedback
            </Button>
          </Stack>
        ) : (
          <Stack>
            {requests.map((request) => (
              <RequestRow
                key={request._id}
                request={request}
                categories={config?.categories ?? []}
                onCopy={handleCopy}
                onResend={setResending}
                onRevoke={handleRevoke}
              />
            ))}
          </Stack>
        )}
      </Stack>

      <RequestDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        eventMeta={eventMeta}
        config={config}
        onCreated={(request) => setRequests((prev) => [request, ...prev])}
      />
      <ResendDialog request={resending} onClose={() => setResending(null)} onSend={handleResend} />
    </Box>
  )
}

export default React.memo(ClientFeedbackPanel)
